import { Icon } from '@iconify/react';
import React from 'react';
import { Link } from 'react-router-dom';
import Spacing from '../../Spacing';
import { FaPhone, FaEnvelope, FaGraduationCap, FaBriefcase, FaAward, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';

export default function DoctorDetailsSection({
  bgUrl,
  imgUrl,
  name,
  department,
  designation,
  description,
  social,
  contactInfo,
  contactInfoHeading,
  degrees,
  degreesHeading,
  experiences,
  experiencesHeading,
  awards,
  awardHeading,
  schedules,
  scheduleHeading,
}) {
  const DoctorIntro = () => (
    <>
      {/* Identity & Department */}
      <div className="cs_mcp_doctor_header">
        <span className="cs_mcp_dept_tag">{department}</span>
        <h2 className="cs_mcp_doc_name">{name}</h2>
        <h4 className="cs_mcp_doc_designation">{designation}</h4>
      </div>

      {/* Social Links */}
      <div className="cs_mcp_doctor_socials">
        {social?.map((item, index) => (
          <Link to={item.href} key={index} className="cs_mcp_social_btn" target="_blank" rel="noreferrer">
            <Icon icon={item.icon} />
          </Link>
        ))}
      </div>

      <Spacing md="30" />

      {/* Bio Description */}
      <p className="cs_mcp_doctor_bio">{description}</p>
    </>
  );

  return (
    <div className="cs_mcp_doctor_details_wrap">
      {bgUrl && (
        <div
          className="cs_mcp_doctor_bg"
          style={{ backgroundImage: `url(${bgUrl})` }}
        />
      )}
      
      <div className="container">
        <div className="row cs_mcp_doctor_row">
          
          {/* Left Column: Image, Contacts, Schedule */}
          <div className="col-lg-5 col-md-12 mb-5 mb-lg-0">
            <div className="cs_mcp_doctor_sidebar">
              {/* Doctor Image Card */}
              <div className="cs_mcp_doctor_img_card">
                <div className="cs_mcp_doctor_img_container">
                  <img src={imgUrl} alt={name} className="w-100" />
                  <div className="cs_mcp_doctor_img_overlay_text">
                    {department}
                  </div>
                </div>
              </div>

              {/* MOBILE ONLY: Doctor Intro */}
              <div className="d-block d-lg-none mt-4">
                <DoctorIntro />
              </div>

              <Spacing md="50" lg="30" />

              {/* Quick Contact Info */}
              <div className="cs_mcp_doctor_box cs_mcp_contact_box">
                <h3 className="cs_mcp_box_title">
                  <FaPhone className="me-2 text-primary" />
                  <span>{contactInfoHeading || "Contact Info"}</span>
                </h3>
                <div className="cs_mcp_contact_list">
                  {contactInfo?.map((item, index) => {
                    const isEmail = item.title?.props?.href?.startsWith('mailto:') || typeof item.title === 'string' && item.title.includes('@');
                    return (
                      <div className="cs_mcp_contact_item" key={index}>
                        <div className="cs_mcp_contact_icon_wrap">
                          {isEmail ? <FaEnvelope /> : <FaPhone />}
                        </div>
                        <div className="cs_mcp_contact_text">
                          {item.title}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Spacing md="30" />

              {/* Appointment Schedules */}
              <div className="cs_mcp_doctor_box cs_mcp_schedule_box">
                <h3 className="cs_mcp_box_title">
                  <FaCalendarAlt className="me-2 text-secondary" />
                  <span>{scheduleHeading || "Appointment Schedules"}</span>
                </h3>
                <div className="cs_mcp_table_responsive">
                  <table className="cs_mcp_schedule_table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Timing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules?.map((item, index) => (
                        <tr key={index} className={item.time === 'OFF' ? 'cs_mcp_off_day' : ''}>
                          <td className="cs_mcp_day_name">{item.day}</td>
                          <td className="cs_mcp_time_slot">{item.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Name, Description, Education, Experience, Awards */}
          <div className="col-lg-7 col-md-12 ps-lg-5">
            <div className="cs_mcp_doctor_main_content">
              {/* DESKTOP ONLY: Doctor Intro */}
              <div className="d-none d-lg-block">
                <DoctorIntro />
              </div>

              <Spacing md="40" />

              {/* Degrees Section */}
              <div className="cs_mcp_info_section">
                <h3 className="cs_mcp_section_title">
                  <FaGraduationCap className="me-2 text-primary" />
                  <span>{degreesHeading || "Degrees"}</span>
                </h3>
                <div className="row">
                  {degrees?.map((item, index) => (
                    <div className="col-md-6 col-12 mb-3" key={index}>
                      <div className="cs_mcp_degree_card">
                        <span className="cs_mcp_degree_abbr">{item.subTitle}</span>
                        <p className="cs_mcp_degree_inst">{item.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Spacing md="40" />

              {/* Experience Section - Timeline */}
              <div className="cs_mcp_info_section">
                <h3 className="cs_mcp_section_title">
                  <FaBriefcase className="me-2 text-secondary" />
                  <span>{experiencesHeading || "Experiences"}</span>
                </h3>
                <div className="cs_mcp_timeline">
                  {experiences?.map((item, index) => (
                    <div className="cs_mcp_timeline_item" key={index}>
                      <div className="cs_mcp_timeline_dot"></div>
                      <div className="cs_mcp_timeline_content">
                        <p className="cs_mcp_timeline_text">{item.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Spacing md="40" />

              {/* Awards Section */}
              <div className="cs_mcp_info_section">
                <h3 className="cs_mcp_section_title">
                  <FaAward className="me-2 text-primary" />
                  <span>{awardHeading || "Awards/Achievements"}</span>
                </h3>
                <div className="cs_mcp_awards_list">
                  {awards?.map((item, index) => (
                    <div className="cs_mcp_award_item" key={index}>
                      <div className="cs_mcp_award_icon_wrap">
                        <FaAward />
                      </div>
                      <div className="cs_mcp_award_text">
                        <p className="cs_mcp_award_title">{item.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
